import S from 's-js';
import Currency from 'lib/Currency';
import { jsonableObservable } from 'lib/jsonableObservable';
import {
    Price as ServerPrice,
    PriceOptions,
    PriceData,
    NullablePrice as ServerNullablePrice,
    NullablePriceOptions,
    NullablePriceData
} from 'server/models/Price'

export { PriceOptions, PriceData, NullablePriceOptions, NullablePriceData } from 'server/models/Price';

export class Price extends ServerPrice {
    constructor();
    constructor(data : PriceOptions);
    constructor(data : PriceOptions | null = null) {
        super(data || { Cur : Currency.USD });
        if (data && data.Cur !== undefined) checkCur(data.Cur);
        const _Cur = this.Cur;
        this.Cur = jsonableObservable(function (v? : string) : string {
            return arguments.length > 0? (checkCur(v!), _Cur(v!)) : _Cur();
        });
    }

    add(num : number) : ImmutablePrice;
    add(p : Price) : ImmutablePrice;
    add(p : Price | number) { return new ImmutablePrice({
        Num: () => {
            const num = p instanceof Price ? (checkCompatibility(this.Cur(), p.Cur()), p.Num()) : p;
            return this.Num() + num;
        },
        Cur: this.Cur
    })}

    subtract(num : number) : ImmutablePrice;
    subtract(p : Price) : ImmutablePrice;
    subtract(p : Price | number) { return new ImmutablePrice({
        Num: () => {
            const num = p instanceof Price ? (checkCompatibility(this.Cur(), p.Cur()), p.Num()) : p;
            return this.Num() - num;
        },
        Cur: this.Cur
    })}

    roundToNearest(rounding : number) { return new ImmutablePrice({
        Num: () => {
            let num = Math.round(this.Num() / rounding) * rounding,
                // due to floating point errors, our result might have more decimal places than our
                // rounding number, so fix that here
                roundingDigits = rounding.toString(),
                decimal = roundingDigits.lastIndexOf('.');
            if (decimal !== -1) {
                let places = roundingDigits.length - decimal;
                num = parseFloat(num.toFixed(places));
            }
            return num;
        },
        Cur: this.Cur
    })}

    roundToNPlaces(places : number) { return new ImmutablePrice({
        Num: () => parseFloat(this.Num().toFixed(places)),
        Cur: this.Cur
    })}

    set(p : Price) {
        this.Num(S.sample(p.Num));
        this.Cur(S.sample(p.Cur));
    }

    toString(opts? : { currency? : 'symbol' | 'full' | false, places? : number }) {
        opts = opts || {};
        const currency = opts.currency !== undefined ? opts.currency : 'symbol',
            places = opts.places !== undefined ? opts.places : -1; // -1 === don't do any rounding
        let num = this.Num(), cur = this.Cur();
        if (places >= 0) num = parseFloat(num.toFixed(places));
        // TH 05/04/2017 -- Using the options parameter to toLocaleString.
        // This seems to be well-supported on modern desktop browsers.
        // On older browsers, the rounding is less predictable.
        // E.g., on IE <= 10, the behavior of toLocaleString seems to be to always
        // round to 2 decimal places regardless of any options passed.
        // See https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Number/toLocaleString
        const numStr = num.toLocaleString(undefined, { minimumFractionDigits: places >= 0 ? places : undefined });
        return (
            // TH 05/04/2017 -- TODO: toLocaleString supports a lot of these formatting options
            // natively. That might not give us as much control as we need though, and might
            // degrade less gracefully on older browsers.
            currency === 'full' ? numStr + ' ' + cur :
            // TH 05/01/2017 -- TODO: Localization to determine placement of symbol.
            // As I understand it, whether to place symbol before or after the number is more
            // a function of the desired locale than of the currency being used.
            // See: https://ux.stackexchange.com/a/22589
            currency === 'symbol' ? Currency.Symbol[cur] + numStr :
            numStr
        );
    }
}

export class NullablePrice extends ServerNullablePrice {
    constructor();
    constructor(data : NullablePriceOptions);
    constructor(data : NullablePriceOptions | null = null) {
        super(data || {});
        if (data && data.Cur !== undefined && data.Cur !== null) checkCur(data.Cur);
        const _Cur = this.Cur;
        this.Cur = jsonableObservable(function (v? : string | null) {
            return arguments.length > 0 ? (v === null || checkCur(v!), _Cur(v!)) : _Cur();
        });
    }

    add(num : number | null) : ImmutableNullablePrice;
    add(p : NullablePrice) : ImmutableNullablePrice;
    add(p : NullablePrice | number | null) { return new ImmutableNullablePrice({
        Num: () => {
            if (this.Num() === null || this.Cur() === null || p === null || p instanceof NullablePrice && (p.Num() === null || p.Cur() === null)) return null;
            const num = p instanceof NullablePrice ? (checkCompatibility(this.Cur()!, p.Cur()!), p.Num()!) : p!;
            return this.Num()! + num;
        },
        Cur: this.Cur
    })}

    subtract(num : number | null) : ImmutableNullablePrice;
    subtract(p : NullablePrice) : ImmutableNullablePrice;
    subtract(p : NullablePrice | number | null) { return new ImmutableNullablePrice({
        Num: () => {
            if (this.Num() === null || this.Cur() === null || p === null || p instanceof ImmutableNullablePrice && (p.Num() === null || p.Cur() === null)) return null;
            const num = p instanceof NullablePrice ? (checkCompatibility(this.Cur()!, p.Cur()!), p.Num()!) : p!;
            return this.Num()! - num;
        },
        Cur: this.Cur
    })}

    roundToNearest(rounding : number) { return new ImmutableNullablePrice({
        Num : () => {
            let orig = this.Num(),
                num = orig === null ? null : Math.round(orig / rounding) * rounding,
                // due to floating point errors, our result might have more decimal places than our
                // rounding number, so fix that here
                roundingDigits = rounding.toString(),
                decimal = roundingDigits.lastIndexOf('.');

            if (decimal !== -1 && num !== null) {
                let places = roundingDigits.length - decimal;
                num = parseFloat(num.toFixed(places));
            }

            return num;
        },
        Cur: this.Cur
    })}

    roundToNPlaces(places : number) { return new ImmutableNullablePrice({
        Num: () => this.Num() === null ? null : parseFloat(this.Num()!.toFixed(places)),
        Cur: this.Cur
    })}

    set(p : NullablePrice) {
        this.Num(S.sample(p.Num));
        this.Cur(S.sample(p.Cur));
    }

    toString(opts? : { currency? : 'symbol' | 'full' | false, places? : number }) {
        opts = opts || {};
        const currency = opts.currency !== undefined ? opts.currency : 'symbol',
            places = opts.places !== undefined ? opts.places : -1; // -1 === don't do any rounding
        let num = this.Num(), cur = this.Cur();
        if (places >= 0 && num !== null) num = parseFloat(num.toFixed(places));
        // TH 05/04/2017 -- Using the options parameter to toLocaleString.
        // This seems to be well-supported on modern desktop browsers.
        // On older browsers, the rounding is less predictable.
        // E.g., on IE <= 10, the behavior of toLocaleString seems to be to always
        // round to 2 decimal places regardless of any options passed.
        // See https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Number/toLocaleString
        const numStr = num === null ? '--' : num.toLocaleString(undefined, { minimumFractionDigits: places >= 0 ? places : undefined });
        return (
            // TH 05/04/2017 -- TODO: toLocaleString supports a lot of these formatting options
            // natively. That might not give us as much control as we need though, and might
            // degrade less gracefully on older browsers.
            currency === 'full' ? numStr + (cur === null ? '' : ' ' + cur) :
            // TH 05/01/2017 -- TODO: Localization to determine placement of symbol.
            // As I understand it, whether to place symbol before or after the number is more
            // a function of the desired locale than of the currency being used.
            // See: https://ux.stackexchange.com/a/22589
            currency === 'symbol' ? (cur === null ? '' : Currency.Symbol[cur]) + numStr :
            numStr
        );
    }
}

interface ImmutablePriceOptions {
    Num : () => number;
    Cur : () => string;
}

export class ImmutablePrice extends Price {
    constructor(data : ImmutablePriceOptions) {
        super();
        this.Num = immutable(data.Num, "Num");
        this.Cur = immutable(data.Cur, "Cur");
    }
}

interface ImmutableNullablePriceOptions {
    Num : () => number | null;
    Cur : () => string | null;
}

export class ImmutableNullablePrice extends NullablePrice {
    constructor(data : ImmutableNullablePriceOptions) {
        super();
        this.Num = immutable(data.Num, "Num");
        this.Cur = immutable(data.Cur, "Cur");
    }
}

function checkCompatibility(cur1 : string, cur2 : string) {
    if (cur1 !== cur2) throw new Error('Cannot combine prices of different currencies');
}

function immutable<T>(val : () => T, name : string) {
    return function () {
        if (arguments.length === 0) return val();
        throw new Error("Cannot modify " + name + " of an immutable price");
    }
}

function checkCur(cur : string) {
    if (!(cur in Currency.Symbol)) {
        throw new Error("'" + cur + "' is not a recognized currency");
    }
}


// WEBPACK FOOTER //
// ./data/models/Price.ts